import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { openReceiptPrint } from "@/lib/receipt";
import { resolvePrimaryOutlet } from "@/lib/outlet";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Plus, X, ShoppingBag, Receipt, IndianRupee, Layers, Search,
    UserRound, Printer,
} from "lucide-react";
import { toast } from "sonner";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

const GOLD = "#C9A227";
const GOLD_SOFT = "#D4AF37";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";

const BILLABLE_STATUSES = ["booked", "confirmed", "arrived", "in-service"];

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const fieldClass =
    "border-[#E8E4DE] bg-[#FFFEF9] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";

const svcBtn =
    "text-left rounded-lg border border-[#E8E4DE] bg-[#FFFEF9] px-3 py-2.5 transition-colors hover:border-[#D4AF37]/55 hover:bg-[#FAF9F6]";

const PAYMENT_BADGE = {
    cash: { bg: "#FDF3DC", text: "#9A7B10" },
    upi: { bg: "#E0EDFA", text: "#3B82C4" },
    card: { bg: "#FBE4EF", text: "#D9468F" },
    razorpay: { bg: "#FDE8EC", text: MAROON },
};

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>◆</span>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
        </div>
    );
}

function SalonCard({ children, className = "" }) {
    return (
        <div className={`relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white shadow-[0_4px_22px_rgba(45,41,38,0.04)] ${className}`}>
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
            {children}
        </div>
    );
}

function PaymentBadge({ mode }) {
    const { bg, text } = PAYMENT_BADGE[mode] ?? { bg: "#FAF9F6", text: CHOCOLATE };
    return (
        <span
            className="inline-block rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide"
            style={{ background: bg, color: text }}
        >
            {mode}
        </span>
    );
}

function fmtTime(iso) {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function makeCartItem(svc, staffList, staffId) {
    return {
        kind: "service",
        ref_id: svc.id,
        name: svc.name,
        qty: 1,
        unit_price: svc.price,
        gst_rate: svc.gst_rate || 18,
        staff_id: staffId || staffList[0]?.id || null,
    };
}

export default function POS() {
    const [services, setServices] = useState([]);
    const [staff, setStaff] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [cart, setCart] = useState([]);
    const [customerId, setCustomerId] = useState("");
    const [appointmentId, setAppointmentId] = useState("");
    const [customerSearch, setCustomerSearch] = useState("");
    const [discount, setDiscount] = useState(0);
    const [paymentMode, setPaymentMode] = useState("cash");
    const [busy, setBusy] = useState(false);
    const [walkInOpen, setWalkInOpen] = useState(false);
    const [invoiceView, setInvoiceView] = useState(null);

    const load = useCallback(async () => {
        const dayStart = new Date();
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);

        const [s, st, c, iv, ap] = await Promise.all([
            api.get("/services"),
            api.get("/staff"),
            api.get("/customers"),
            api.get("/invoices"),
            api.get("/appointments", {
                params: { date_from: dayStart.toISOString(), date_to: dayEnd.toISOString() },
            }),
        ]);
        setServices(s.data);
        setStaff(st.data);
        setCustomers(c.data);
        setInvoices(iv.data);
        setAppointments(ap.data);
    }, []);

    useEffect(() => { load(); }, [load]);

    const selectedCustomer = useMemo(
        () => customers.find((c) => c.id === customerId),
        [customers, customerId],
    );

    const filteredCustomers = useMemo(() => {
        const q = customerSearch.trim().toLowerCase();
        if (!q) return [];
        return customers.filter(
            (c) => c.full_name?.toLowerCase().includes(q) || c.mobile?.includes(q),
        ).slice(0, 6);
    }, [customers, customerSearch]);

    const todayBillable = useMemo(
        () => appointments.filter(
            (a) => !a.invoice_id && BILLABLE_STATUSES.includes(a.status),
        ),
        [appointments],
    );

    const addItem = (svc) => {
        setAppointmentId("");
        setCart((prev) => {
            const idx = prev.findIndex((it) => it.kind === "service" && it.ref_id === svc.id);
            if (idx >= 0) {
                return prev.map((it, i) => (i === idx ? { ...it, qty: it.qty + 1 } : it));
            }
            return [...prev, makeCartItem(svc, staff, staff[0]?.id)];
        });
    };

    const loadFromAppointment = (appt, { silent = false } = {}) => {
        const items = (appt.items || []).map((it) => {
            const svc = services.find((s) => s.id === it.service_id);
            return {
                kind: "service",
                ref_id: it.service_id,
                name: it.service_name || svc?.name || "Service",
                qty: 1,
                unit_price: it.price ?? svc?.price ?? 0,
                gst_rate: it.gst_rate ?? svc?.gst_rate ?? 18,
                staff_id: it.staff_id || staff[0]?.id || null,
            };
        });
        if (items.length === 0) {
            toast.error("Appointment has no services");
            return;
        }
        setCustomerId(appt.customer_id);
        setAppointmentId(appt.id);
        setCart(items);
        setCustomerSearch("");
        if (!silent) toast.success("Appointment loaded");
    };

    const handleWalkInCheckedIn = (customer, appt) => {
        setCustomers((prev) => [customer, ...prev.filter((c) => c.id !== customer.id)]);
        const enriched = {
            ...appt,
            customer: { id: customer.id, full_name: customer.full_name, mobile: customer.mobile },
        };
        setAppointments((prev) => [enriched, ...prev.filter((a) => a.id !== appt.id)]);
        loadFromAppointment(enriched, { silent: true });
        setWalkInOpen(false);
    };

    const updateCart = (i, patch) => {
        setCart((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
    };

    const removeItem = (i) => setCart((prev) => prev.filter((_, idx) => idx !== i));

    const subTotal = cart.reduce((s, it) => s + it.unit_price * it.qty, 0);
    const gstTotal = cart.reduce((s, it) => s + (it.unit_price * it.qty * it.gst_rate) / 100, 0);
    const grand = Math.max(0, subTotal + gstTotal - Number(discount || 0));

    const resetBill = () => {
        setCart([]);
        setCustomerId("");
        setAppointmentId("");
        setDiscount(0);
        setCustomerSearch("");
    };

    const launchRazorpay = async (inv) => {
        const orderRes = await api.post("/payments/order", {
            amount: inv.grand_total,
            invoice_id: inv.id,
        });
        const { order_id, amount, currency, key_id } = orderRes.data;
        return new Promise((resolve) => {
            const options = {
                key: key_id,
                amount,
                currency,
                order_id,
                name: "BookMySaloon",
                description: `Invoice ${inv.invoice_number}`,
                handler: async (res) => {
                    try {
                        await api.post("/payments/verify", {
                            ...res, invoice_id: inv.id,
                        });
                        toast.success("Payment successful");
                        const full = await api.get(`/invoices/${inv.id}`);
                        setInvoiceView(full.data);
                    } catch {
                        toast.error("Verification failed");
                    } finally {
                        resolve();
                    }
                },
                modal: { ondismiss: resolve },
                theme: { color: MAROON },
            };
            // eslint-disable-next-line no-undef
            const rzp = new window.Razorpay(options);
            rzp.open();
        });
    };

    const checkout = async () => {
        if (!customerId) {
            toast.error("Select a customer");
            return;
        }
        if (cart.length === 0) {
            toast.error("Add at least one service");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                customer_id: customerId,
                items: cart,
                discount: Number(discount || 0),
                payment_mode: paymentMode,
            };
            if (appointmentId) payload.appointment_id = appointmentId;

            const r = await api.post("/invoices", payload);
            const inv = r.data;

            if (paymentMode === "razorpay") {
                await launchRazorpay(inv);
            } else {
                toast.success(`Invoice ${inv.invoice_number} paid`);
                const full = await api.get(`/invoices/${inv.id}`);
                setInvoiceView(full.data);
            }
            resetBill();
            load();
        } catch {
            toast.error("Checkout failed");
        } finally {
            setBusy(false);
        }
    };

    const openInvoice = async (id) => {
        try {
            const r = await api.get(`/invoices/${id}`);
            setInvoiceView(r.data);
        } catch {
            toast.error("Could not load invoice");
        }
    };

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="pos-page">
            <header>
                <p className={EYEBROW} style={{ color: GOLD }}>Billing</p>
                <h1 className="mt-1.5 text-[1.375rem] font-bold leading-snug sm:text-[1.5rem]" style={{ color: CHOCOLATE }}>
                    Point of Sale
                </h1>
                <GoldDivider className="mt-3 max-w-[200px]" />
                <p className="mt-3 flex flex-wrap items-center gap-2 text-sm font-semibold text-[#8A8178]">
                    <Layers className="size-4 text-[#D4AF37]" />
                    {services.length} service{services.length !== 1 ? "s" : ""}
                    <span className="text-[#E8E4DE]">|</span>
                    <Receipt className="size-4 text-[#D4AF37]" />
                    {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
                    {cart.length > 0 && (
                        <>
                            <span className="text-[#E8E4DE]">|</span>
                            <ShoppingBag className="size-4 text-[#D4AF37]" />
                            <span style={{ color: CHOCOLATE }}>{cart.length} in cart</span>
                        </>
                    )}
                </p>
            </header>

            <div className="grid gap-6 lg:grid-cols-5">
                <div className="space-y-5 lg:col-span-3">
                    <SalonCard>
                        <div className="p-5">
                            <h2 className="mb-3 text-lg font-bold" style={{ color: CHOCOLATE }}>Catalogue</h2>
                            {todayBillable.length > 0 && (
                                <div className="mb-3 flex flex-wrap gap-1.5">
                                    {todayBillable.map((a) => (
                                        <button
                                            key={a.id}
                                            type="button"
                                            onClick={() => loadFromAppointment(a)}
                                            className="rounded-full border border-[#E8E4DE] bg-[#FFFEF9] px-2.5 py-1 text-[11px] font-semibold text-[#3D2817] transition-colors hover:border-[#D4AF37]/55"
                                            data-testid={`pos-appt-${a.id}`}
                                            title={(a.items || []).map((it) => it.service_name).join(", ")}
                                        >
                                            {fmtTime(a.start_at)} · {a.customer?.full_name?.split(" ")[0] || "Guest"}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="scrollbar-salon grid max-h-[420px] grid-cols-2 gap-2 overflow-y-auto pr-1 md:grid-cols-3">
                                {services.map((s) => (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => addItem(s)}
                                        className={svcBtn}
                                        data-testid={`pos-add-${s.id}`}
                                    >
                                        <div className="truncate text-sm font-semibold" style={{ color: CHOCOLATE }}>{s.name}</div>
                                        <div className="mt-0.5 text-xs font-medium">
                                            <span style={{ color: CHOCOLATE }}>{inr(s.price)}</span>
                                            <span className="text-[#8A8178]"> · {s.duration_min}m</span>
                                        </div>
                                    </button>
                                ))}
                                {services.length === 0 && (
                                    <p className="col-span-full py-8 text-center text-sm font-medium text-[#8A8178]">
                                        No services in catalogue.
                                    </p>
                                )}
                            </div>
                        </div>
                    </SalonCard>

                    <SalonCard>
                        <div className="p-5">
                            <h2 className="mb-3 text-lg font-bold" style={{ color: CHOCOLATE }}>Recent invoices</h2>
                            <div className="scrollbar-salon max-h-72 space-y-0 overflow-y-auto pr-1">
                                {invoices.slice(0, 20).map((iv) => (
                                    <button
                                        key={iv.id}
                                        type="button"
                                        onClick={() => openInvoice(iv.id)}
                                        className="flex w-full items-center justify-between border-b border-[#E8E4DE]/80 py-3 text-left text-sm transition-colors last:border-0 hover:bg-[#FAF9F6]/60"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-semibold" style={{ color: CHOCOLATE }}>{iv.invoice_number}</div>
                                            <div className="mt-0.5 truncate text-xs font-medium text-[#8A8178]">
                                                {iv.customer?.full_name} · {new Date(iv.created_at).toLocaleString("en-IN")}
                                            </div>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <div className="font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(iv.grand_total)}</div>
                                            <div className="mt-1">
                                                <PaymentBadge mode={iv.payment_mode} />
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                {invoices.length === 0 && (
                                    <p className="py-8 text-center text-sm font-medium text-[#8A8178]">No invoices yet</p>
                                )}
                            </div>
                        </div>
                    </SalonCard>
                </div>

                <aside className="space-y-5 lg:col-span-2">
                    <SalonCard className="lg:sticky lg:top-4">
                        <div className="p-5">
                            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold" style={{ color: CHOCOLATE }}>
                                <ShoppingBag className="size-5" style={{ color: GOLD }} />
                                Current bill
                            </h2>

                            <div className="mb-4 space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>
                                    Customer
                                </Label>

                                {selectedCustomer ? (
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="min-w-0 truncate font-semibold" style={{ color: CHOCOLATE }}>
                                            {selectedCustomer.full_name}
                                        </span>
                                        <span className="shrink-0 text-[#8A8178]">· {selectedCustomer.mobile}</span>
                                        <button
                                            type="button"
                                            onClick={() => { setCustomerId(""); setAppointmentId(""); }}
                                            className="ml-auto shrink-0 text-[#B8859C] hover:text-[#4A0E17]"
                                            aria-label="Clear customer"
                                        >
                                            <X className="size-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-[#8A8178]" />
                                        <Input
                                            value={customerSearch}
                                            onChange={(e) => setCustomerSearch(e.target.value)}
                                            placeholder="Search name or mobile…"
                                            className={`h-9 pl-9 text-sm ${fieldClass}`}
                                            data-testid="pos-customer-search"
                                        />
                                    </div>
                                )}

                                {!selectedCustomer && filteredCustomers.length > 0 && (
                                    <div className="rounded-md border border-[#E8E4DE] bg-white">
                                        {filteredCustomers.map((c) => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => {
                                                    setCustomerId(c.id);
                                                    setCustomerSearch("");
                                                    setAppointmentId("");
                                                }}
                                                className="flex w-full items-center justify-between border-b border-[#E8E4DE]/60 px-2.5 py-1.5 text-left text-xs last:border-0 hover:bg-[#FAF9F6]"
                                                data-testid={`pos-customer-pick-${c.id}`}
                                            >
                                                <span className="truncate font-medium" style={{ color: CHOCOLATE }}>{c.full_name}</span>
                                                <span className="ml-2 shrink-0 text-[#8A8178]">{c.mobile}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {!selectedCustomer && (
                                    <Dialog open={walkInOpen} onOpenChange={setWalkInOpen}>
                                        <DialogTrigger asChild>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className={navBtn}
                                                data-testid="pos-walk-in"
                                            >
                                                <UserRound className="mr-1 size-3.5" /> Walk-in check-in
                                            </Button>
                                        </DialogTrigger>
                                        <WalkInCheckInDialog
                                            services={services}
                                            staff={staff}
                                            onClose={() => setWalkInOpen(false)}
                                            onCheckedIn={handleWalkInCheckedIn}
                                        />
                                    </Dialog>
                                )}
                            </div>

                            <div className="scrollbar-salon max-h-72 space-y-1.5 overflow-y-auto pr-1">
                                {cart.length === 0 && (
                                    <div className="rounded-lg border border-dashed border-[#E8E4DE] bg-[#FAF9F6]/60 px-4 py-10 text-center">
                                        <Plus className="mx-auto mb-2 size-5 text-[#D4AF37]" />
                                        <p className="text-sm font-medium text-[#8A8178]">
                                            Tap services on the left to add them.
                                        </p>
                                    </div>
                                )}
                                {cart.map((it, i) => (
                                    <div
                                        key={`${it.ref_id}-${i}`}
                                        className="rounded-lg border border-[#E8E4DE]/80 bg-[#FFFEF9] px-2 py-2 text-sm"
                                    >
                                        <div className="grid grid-cols-12 items-center gap-2">
                                            <div className="col-span-5 truncate font-semibold" style={{ color: CHOCOLATE }} title={it.name}>
                                                {it.name}
                                            </div>
                                            <Input
                                                type="number"
                                                min={1}
                                                value={it.qty}
                                                onChange={(e) => updateCart(i, { qty: Math.max(1, +e.target.value || 1) })}
                                                className={`col-span-2 h-8 text-center ${fieldClass}`}
                                                data-testid={`pos-qty-${i}`}
                                            />
                                            <Input
                                                type="number"
                                                value={it.unit_price}
                                                onChange={(e) => updateCart(i, { unit_price: +e.target.value })}
                                                className={`col-span-3 h-8 text-right ${fieldClass}`}
                                            />
                                            <span className="col-span-1 text-right text-xs font-bold tabular-nums" style={{ color: CHOCOLATE }}>
                                                {inr(it.unit_price * it.qty)}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => removeItem(i)}
                                                className="col-span-1 flex justify-center text-[#B8859C] hover:text-[#4A0E17]"
                                            >
                                                <X className="size-3.5" />
                                            </button>
                                        </div>
                                        {staff.length > 0 && (
                                            <Select
                                                value={it.staff_id || ""}
                                                onValueChange={(v) => updateCart(i, { staff_id: v })}
                                            >
                                                <SelectTrigger className={`mt-1.5 h-7 text-xs ${fieldClass}`} data-testid={`pos-staff-${i}`}>
                                                    <SelectValue placeholder="Stylist" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {staff.map((s) => (
                                                        <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 space-y-2 border-t border-[#E8E4DE] pt-4 text-sm">
                                <Row label="Sub-total" value={inr(subTotal)} />
                                <Row label="GST" value={inr(gstTotal)} />
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs font-bold uppercase tracking-wide text-[#8A8178]">Discount</Label>
                                    <Input
                                        type="number"
                                        value={discount}
                                        onChange={(e) => setDiscount(e.target.value)}
                                        className={`h-8 w-24 text-right ${fieldClass}`}
                                        data-testid="pos-discount"
                                    />
                                </div>
                                <div className="flex items-center justify-between border-t border-[#E8E4DE] pt-3">
                                    <span className="flex items-center gap-1.5 text-lg font-bold" style={{ color: CHOCOLATE }}>
                                        <IndianRupee className="size-4" style={{ color: GOLD }} />
                                        Total
                                    </span>
                                    <span className="text-2xl font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(grand)}</span>
                                </div>
                            </div>

                            <div className="mt-4 space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Payment</Label>
                                <Select value={paymentMode} onValueChange={setPaymentMode}>
                                    <SelectTrigger className={fieldClass} data-testid="pos-payment-mode">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cash">Cash</SelectItem>
                                        <SelectItem value="upi">UPI (manual)</SelectItem>
                                        <SelectItem value="card">Card (manual)</SelectItem>
                                        <SelectItem value="razorpay">Razorpay (UPI/Card)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <Button
                                className="mt-4 w-full bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]"
                                onClick={checkout}
                                disabled={busy || cart.length === 0}
                                data-testid="pos-checkout-button"
                            >
                                <Receipt className="mr-1 size-4" /> {busy ? "Processing…" : "Checkout"}
                            </Button>
                        </div>
                    </SalonCard>
                </aside>
            </div>

            <InvoiceDialog invoice={invoiceView} onClose={() => setInvoiceView(null)} />
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between font-medium">
            <span className="text-[#8A8178]">{label}</span>
            <span className="tabular-nums" style={{ color: CHOCOLATE }}>{value}</span>
        </div>
    );
}

function WalkInCheckInDialog({ services, staff, onClose, onCheckedIn }) {
    const [f, setF] = useState({ full_name: "", mobile: "" });
    const [serviceId, setServiceId] = useState("");
    const [staffId, setStaffId] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setF({ full_name: "", mobile: "" });
        setServiceId("");
        setStaffId("");
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!serviceId) {
            toast.error("Select a service");
            return;
        }
        setBusy(true);
        try {
            const custRes = await api.post("/customers", {
                full_name: f.full_name.trim(),
                mobile: f.mobile.trim(),
                gender: "female",
            });
            const customer = custRes.data;

            const apptRes = await api.post("/appointments", {
                customer_id: customer.id,
                // Backend rejects appointments scheduled in the past; add a small buffer
                // to avoid race conditions between client/server clocks.
                start_at: new Date(Date.now() + 2 * 60 * 1000).toISOString(),
                items: [{ service_id: serviceId, staff_id: staffId || null }],
                source: "walk-in",
            });
            const appt = apptRes.data;

            await api.patch(`/appointments/${appt.id}/status`, { status: "arrived" });
            appt.status = "arrived";

            toast.success(
                customer.full_name === f.full_name.trim()
                    ? "Walk-in checked in"
                    : "Existing client matched — checked in",
            );
            reset();
            onCheckedIn(customer, appt);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Walk-in check-in failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogContent className="max-w-sm rounded-xl border-[#E8E4DE] p-5" data-testid="pos-walk-in-dialog">
            <DialogHeader>
                <DialogTitle className="text-lg font-bold" style={{ color: MAROON }}>Walk-in check-in</DialogTitle>
                <p className="text-xs font-medium text-[#8A8178]">
                    Register client, book today&apos;s visit, and load the bill.
                </p>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 pt-1">
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Name</Label>
                    <Input
                        className={fieldClass}
                        value={f.full_name}
                        onChange={(e) => setF({ ...f, full_name: e.target.value })}
                        required
                        autoFocus
                        data-testid="pos-walk-in-name"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Mobile</Label>
                    <Input
                        className={fieldClass}
                        value={f.mobile}
                        onChange={(e) => setF({ ...f, mobile: e.target.value })}
                        required
                        inputMode="tel"
                        data-testid="pos-walk-in-mobile"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Service</Label>
                    <Select value={serviceId} onValueChange={setServiceId}>
                        <SelectTrigger className={fieldClass} data-testid="pos-walk-in-service">
                            <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                            {services.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                    {s.name} · {inr(s.price)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {staff.length > 0 && (
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Stylist</Label>
                        <Select value={staffId} onValueChange={setStaffId}>
                            <SelectTrigger className={fieldClass} data-testid="pos-walk-in-staff">
                                <SelectValue placeholder="Select stylist" />
                            </SelectTrigger>
                            <SelectContent>
                                {staff.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" className={navBtn} onClick={onClose}>Cancel</Button>
                    <Button
                        type="submit"
                        className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]"
                        disabled={busy || services.length === 0}
                        data-testid="pos-walk-in-submit"
                    >
                        {busy ? "Checking in…" : "Check in & bill"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}

function InvoiceDialog({ invoice, onClose }) {
    const { user } = useAuth();
    const [outlet, setOutlet] = useState(null);

    useEffect(() => {
        if (!invoice) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/outlets");
                const match = resolvePrimaryOutlet(data, invoice.outlet_id);
                if (!cancelled) setOutlet(match || null);
            } catch {
                if (!cancelled) setOutlet(null);
            }
        })();
        return () => { cancelled = true; };
    }, [invoice?.id, invoice?.outlet_id]);

    if (!invoice) return null;

    const cust = invoice.customer || {};
    const items = invoice.items || [];

    const printInvoice = () => {
        const ok = openReceiptPrint(invoice, {
            name: user?.tenant_name || outlet?.name || "Salon & Spa",
            address: outlet?.address,
            city: outlet?.city,
            phone: outlet?.phone,
            gstin: outlet?.gstin,
        });
        if (!ok) toast.error("Allow pop-ups to print the receipt");
    };

    return (
        <Dialog open={!!invoice} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-sm rounded-xl border-[#E8E4DE] p-5" data-testid="pos-invoice-dialog">
                <DialogHeader>
                    <DialogTitle className="text-lg font-bold" style={{ color: MAROON }}>
                        {invoice.invoice_number}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-2 pt-1 text-sm">
                    <p className="font-medium text-[#8A8178]">
                        {new Date(invoice.created_at).toLocaleString("en-IN")}
                    </p>
                    <p className="font-semibold" style={{ color: CHOCOLATE }}>
                        {cust.full_name} · {cust.mobile}
                    </p>
                    <div className="rounded-md border border-[#E8E4DE] bg-[#FFFEF9] text-xs">
                        {items.map((it, i) => (
                            <div
                                key={i}
                                className={`flex justify-between gap-2 px-2.5 py-2 ${i < items.length - 1 ? "border-b border-[#E8E4DE]/80" : ""}`}
                            >
                                <span className="min-w-0 truncate font-medium" style={{ color: CHOCOLATE }}>{it.name}</span>
                                <span className="shrink-0 tabular-nums text-[#8A8178]">×{it.qty}</span>
                                <span className="shrink-0 font-bold tabular-nums" style={{ color: CHOCOLATE }}>
                                    {inr(it.unit_price * it.qty)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <Row label="Sub-total" value={inr(invoice.sub_total)} />
                    <Row label="GST" value={inr((invoice.cgst || 0) + (invoice.sgst || 0))} />
                    {invoice.discount > 0 && <Row label="Discount" value={inr(invoice.discount)} />}
                    <div className="flex justify-between border-t border-[#E8E4DE] pt-2 text-base font-bold">
                        <span style={{ color: CHOCOLATE }}>Total</span>
                        <span className="tabular-nums" style={{ color: CHOCOLATE }}>{inr(invoice.grand_total)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[#8A8178]">Payment</span>
                        <PaymentBadge mode={invoice.payment_mode} />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" size="sm" className={navBtn} onClick={onClose}>Close</Button>
                    <Button type="button" size="sm" className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]" onClick={printInvoice}>
                        <Printer className="mr-1 size-3.5" /> Print
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
